import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { Checkbox } from '@repo/ui/checkbox'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { Switch } from '@repo/ui/switch'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Cfl, cn } from '@repo/ui/utils'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useMemo } from 'react'

import { useAbility } from '@/ability/ability-context'
import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { DataTable } from '@/components/admin/members/data-table'
import { Loader } from '@/components/beneficiary/list/Loader'
import { RootState } from '@/store'
import {
  assignCaseWorkerToBeneficiary,
  unassignCaseWorkerFromBeneficiary,
} from '@/store/case-workers/thunks'
import { useStoreDispatch } from '@/store/hooks'
import { CaseWorker } from '@/store/user'

export function CaseWorkerListWrapper(
  props: Pick<CaseWorkerListProps, 'assignedCaseWorkerIds' | 'beneficiaryId'>,
) {
  const ability = useAbility()
  if (!ability.can('accessRoute', 'admin')) return null

  const { data, isFulfilled, isLoading } = useAsyncData([
    (state: RootState) => state.caseWorkerList.caseWorkers,
  ])
  if (isLoading) return <Loader />
  if (isFulfilled) {
    const caseWorkers = data[0]
    return <CaseWorkerList {...props} caseWorkers={caseWorkers} />
  }
}

type CaseWorkerListProps = {
  assignedCaseWorkerIds: string[]
  caseWorkers: CaseWorker[]
  beneficiaryId: string
}

function CaseWorkerList({
  assignedCaseWorkerIds,
  caseWorkers,
  beneficiaryId,
}: CaseWorkerListProps) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const rowSelection = useMemo(
    () =>
      caseWorkers.reduce((acc: RowSelectionState, v) => {
        acc[v.id] = assignedCaseWorkerIds.some((id) => id === v.id)
        return acc
      }, {}),
    [assignedCaseWorkerIds, caseWorkers],
  )

  const handleAssignOrUnassign = ({
    assign,
    caseWorkerId,
  }: {
    assign: boolean
    caseWorkerId: string
  }) => {
    if (assign) {
      void dispatch(
        assignCaseWorkerToBeneficiary({
          beneficiaryId,
          caseWorkerId,
        }),
      )
    } else {
      void dispatch(
        unassignCaseWorkerFromBeneficiary({
          beneficiaryId,
          caseWorkerId,
        }),
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tc('manageAssignations', { cfl: true })}</CardTitle>
        <CardDescription>{tc('member', { cfl: true, count: caseWorkers.length })}</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns(handleAssignOrUnassign, tc)}
          data={caseWorkers}
          initialState={{
            rowSelection,
            sorting: [
              {
                id: 'lastName',
                desc: false,
              },
            ],
          }}
        />
      </CardContent>
    </Card>
  )
}

const columns = (
  onAssignOrUnassign: (p: { assign: boolean; caseWorkerId: string }) => void,
  tc: (key: string, options?: Record<string, unknown>) => string,
): ColumnDef<CaseWorker>[] => [
  {
    id: 'id',
    header: ({ table }) => (
      <TooltipWrapper
        content={
          table.getIsAllRowsSelected()
            ? tc('unassignAll', { cfl: true })
            : tc('assignAll', { cfl: true })
        }
      >
        <div className="flex w-fit items-center gap-2">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              const ids = table.getFilteredRowModel().rows.map((row) => row.original.id)
              ids.forEach((id) => {
                onAssignOrUnassign({ assign: !!value, caseWorkerId: id })
              })
              table.toggleAllPageRowsSelected(!!value)
            }}
            tabIndex={-1}
          />
          <div className="w-fit text-xs">
            {tc('xAssigned', { count: table.getFilteredSelectedRowModel().rows.length })}
          </div>
        </div>
      </TooltipWrapper>
    ),
    cell: ({ row }) => {
      return (
        <Switch
          checked={row.getIsSelected()}
          tabIndex={-1}
          onCheckedChange={(value) => {
            row.toggleSelected(!!value)
            onAssignOrUnassign({ assign: !!value, caseWorkerId: row.original.id })
          }}
        />
      )
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} options={{ sortable: true, searchable: true }} />
      )
    },
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} options={{ sortable: true, searchable: true }} />
      )
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} options={{ sortable: true, searchable: true }} />
      )
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{
            sortable: true,
            filterable: ['pending', 'enabled', 'disabled'],
          }}
        />
      )
    },
    cell: ({ row, column }) => {
      const value = row.getValue<string>(column.id)
      return (
        <div
          className={cn(
            value === 'pending' && 'text-orange-500',
            value === 'disabled' && 'opacity-50',
          )}
        >
          {Cfl(value)}
        </div>
      )
    },
  },
  {
    accessorKey: 'role',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, filterable: ['admin', 'case worker'] }}
        />
      )
    },
    cell: ({ row, column }) => {
      const value = row.getValue<string>(column.id)
      return <div className={cn(value === 'admin' && 'text-highlight')}>{Cfl(value)}</div>
    },
  },
]
